import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  Box,
  Divider,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ReactNode } from "react";

interface PageTitleBarProps {
  title: ReactNode;
  titlePrefix?: string;
  children?: ReactNode;
  showDivider?: boolean;
  variant?: "small" | "big";
  onBack?: () => void;
}

/**
 * Reusable page title bar component for consistent title styling across routes.
 * Works for both secondary drawer routes and full screen routes.
 *
 * @param title - The title text to display
 * @param children - Optional content to display after the title (e.g., action buttons)
 * @param showDivider - Whether to show a divider below the title bar (default: false for secondary drawer routes, true for full screen routes)
 */
export default function PageTitleBar({
  title,
  titlePrefix,
  children,
  showDivider = false,
  variant = "small",
  onBack,
}: PageTitleBarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const titleFontSize = variant === "big" ? "1.5rem" : "1rem";
  const titleFontWeight = variant === "big" ? 700 : 600;

  return (
    <>
      {/* Desktop header */}
      {!isMobile && (
        <Header>
          <TitleContainer>
            <Typography
              variant={variant === "big" ? "h4" : "h6"}
              component="h1"
              sx={{
                fontSize: titleFontSize,
                fontWeight: titleFontWeight,
              }}
            >
              {titlePrefix && (
                <Box
                  component="span"
                  sx={{ color: "text.secondary", fontWeight: 400 }}
                >
                  {titlePrefix}{" "}
                </Box>
              )}
              {title}
            </Typography>
          </TitleContainer>
          <ChildrenContainer>
            {onBack && (
              <IconButton
                onClick={onBack}
                size="small"
                sx={{
                  backgroundColor: "#F1EFF7",
                  color: "#7D5DA3",
                  "&:hover": { backgroundColor: "#E5E1F0" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {children}
          </ChildrenContainer>
        </Header>
      )}

      {/* Mobile header */}
      {isMobile && (
        <Box
          sx={{
            minHeight: "calc(56px + env(safe-area-inset-top))",
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-start",
            paddingTop: "calc(env(safe-area-inset-top) + 12px)",
            paddingBottom: "12px",
            px: 3,
            mb: showDivider ? 0 : 3,
            backgroundColor: "white",
          }}
        >
          <Typography
            variant={variant === "big" ? "h4" : "h6"}
            component="h1"
            sx={{
              mb: 0,
              fontSize: titleFontSize,
              fontWeight: titleFontWeight,
              textAlign: "left",
              flex: 1,
              lineHeight: 1.2,
            }}
          >
            {titlePrefix && (
              <Box
                component="span"
                sx={{ color: "text.secondary", fontWeight: 400 }}
              >
                {titlePrefix}{" "}
              </Box>
            )}
            {title}
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
            }}
          >
            {onBack && (
              <IconButton
                onClick={onBack}
                sx={{
                  backgroundColor: "#F1EFF7",
                  color: "#7D5DA3",
                  "&:hover": { backgroundColor: "#E5E1F0" },
                }}
              >
                <ArrowBackIcon />
              </IconButton>
            )}
            {children}
          </Box>
        </Box>
      )}

      {showDivider && (
        <Divider
          sx={{
            borderColor: "rgba(222, 222, 222, 1)",
            "&::before, &::after": {
              borderColor: "rgba(222, 222, v, 1) !important",
            },
          }}
        />
      )}
    </>
  );
}

const Header = styled("div")({
  backgroundColor: "white",
  padding: "24px",
  paddingTop: "calc(env(safe-area-inset-top) + 16px)",
  paddingBottom: "16px",
  display: "flex",
  alignItems: "center",
  zIndex: 1,
});

const TitleContainer = styled("div")({
  flex: 1,
  textAlign: "left",
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
});

const ChildrenContainer = styled("div")({
  display: "flex",
  alignItems: "center",
  gap: "8px",
});
